/**
 * Tasks → Stats.
 *
 * Renders the counters + by-type breakdown returned by
 * GET /api/v1/tasks/stats. M5 ships the panel layout per design
 * README 5.5; the 30-day chart and dual-surface search lands during
 * M13 polish (the streak rollup that fills `streaks.inboxZeroTasks`
 * is itself M9 work).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

export const Route = createFileRoute('/tasks/stats')({
  component: TasksStatsView,
});

interface StatsResponse {
  counters: {
    dueToday: number;
    dueWeek: number;
    dueMonth: number;
    completedToday: number;
    completedWeek: number;
    completedMonth: number;
    completedLastWeek: number;
    completedLastMonth: number;
  };
  breakdown: { byType: Record<string, number> };
  chart: { dates: string[]; completedCounts: number[] };
  streaks: { inboxZeroTasks: { current: number; longest: number } };
}

function TasksStatsView() {
  const { getToken } = useAuth();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tasks', 'stats'],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('No active Clerk session');
      return apiRequest<StatsResponse>('/api/v1/tasks/stats', { token });
    },
  });

  if (isLoading) return <p className="mono-label text-mx-grey-mid">LOADING…</p>;
  if (isError || !data) return <p className="text-mx-body-sm text-mx-neg">Could not load stats.</p>;

  return (
    <div className="flex flex-col gap-mx-5">
      <section className="grid grid-cols-4 gap-mx-3">
        <Tile label="Done · today" value={data.counters.completedToday} />
        <Tile label="Done · this week" value={data.counters.completedWeek} />
        <Tile label="Due today" value={data.counters.dueToday} />
        <Tile label="Inbox-zero streak" value={data.streaks.inboxZeroTasks.current} />
      </section>

      <section>
        <p className="eyebrow mono-label mb-mx-3 text-mx-grey-mid">
          <span className="num">01</span>
          By type
        </p>
        <ul className="flex flex-col gap-mx-2">
          {Object.entries(data.breakdown.byType).map(([type, count]) => (
            <li
              key={type}
              className="flex items-center justify-between border-b border-mx-grey-800 py-mx-2"
            >
              <span className="mono-label text-mx-grey-300">{type}</span>
              <span className="tnum text-mx-body text-mx-white">{count}</span>
            </li>
          ))}
        </ul>
      </section>

      <p className="text-mx-body-sm text-mx-grey-mid">
        The 30-day chart and full search land in milestone 13 polish; the inbox-zero streak fills in
        once milestone 9 ships the rollup job.
      </p>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-mx-1 border border-mx-grey-800 bg-mx-card p-mx-3">
      <p className="mono-label text-mx-grey-mid">{label}</p>
      <p className="tnum font-display text-mx-h2 text-mx-white">{value}</p>
    </div>
  );
}
